import { useState } from "react";
import { useOutletContext, Navigate, useParams } from "react-router-dom";
import { CardContent } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2 } from "lucide-react";
import { trpc } from "@/trpc";
import type { ContactDetailContext } from "../ContactDetailPage";
import {
  MemberPosition,
  MEMBER_CATEGORIES,
  MEMBER_STATUSES,
  MEMBER_POSITIONS,
} from "@satyrsmc/shared/client";
import type { MemberCategory, MemberStatus } from "@satyrsmc/shared/client";
import { MEMBER_CATEGORY_LABELS, MEMBER_STATUS_LABELS } from "@/components/members/memberUtils";
import type {
  MembershipTenure,
  OfficerTenure,
  LoaTenure,
} from "@satyrsmc/shared/dto/admin/memberTenure";

export function ContactMemberTab() {
  const { id } = useParams<{ id: string }>();
  const { memberData } = useOutletContext<ContactDetailContext>();

  if (memberData === undefined) return null;
  if (memberData === null) return <Navigate to={`/admin/contacts/${id}`} replace />;

  return <ContactMemberTabContent memberId={memberData.id} memberData={memberData} />;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function extractMessage(err: unknown): string {
  if (err && typeof err === "object" && "message" in err)
    return String((err as { message: unknown }).message);
  return "An error occurred";
}

function ContactMemberTabContent({
  memberId,
  memberData,
}: {
  memberId: string;
  memberData: NonNullable<ContactDetailContext["memberData"]>;
}) {
  const utils = trpc.useUtils();
  const [tenureError, setTenureError] = useState<string | null>(null);

  const invalidate = () => {
    utils.admin.members.getByContactId.invalidate();
    utils.admin.members.get.invalidate({ id: memberId });
    utils.admin.memberTenures.membership.list.invalidate({ member_id: memberId });
    utils.admin.memberTenures.officer.list.invalidate({ member_id: memberId });
    utils.admin.memberTenures.loa.list.invalidate({ member_id: memberId });
  };

  const onTenureError = (err: unknown) => setTenureError(extractMessage(err));

  const updateMember = trpc.admin.members.update.useMutation({ onSuccess: invalidate });

  const { data: membershipTenures = [] } = trpc.admin.memberTenures.membership.list.useQuery({
    member_id: memberId,
  });
  const { data: officerTenures = [] } = trpc.admin.memberTenures.officer.list.useQuery({
    member_id: memberId,
  });
  const { data: loaTenures = [] } = trpc.admin.memberTenures.loa.list.useQuery({
    member_id: memberId,
  });

  const createMembership = trpc.admin.memberTenures.membership.create.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });
  const updateMembership = trpc.admin.memberTenures.membership.update.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });
  const deleteMembership = trpc.admin.memberTenures.membership.delete.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });

  const createOfficer = trpc.admin.memberTenures.officer.create.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });
  const updateOfficer = trpc.admin.memberTenures.officer.update.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });
  const deleteOfficer = trpc.admin.memberTenures.officer.delete.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });

  const createLoa = trpc.admin.memberTenures.loa.create.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });
  const updateLoa = trpc.admin.memberTenures.loa.update.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });
  const deleteLoa = trpc.admin.memberTenures.loa.delete.useMutation({
    onSuccess: invalidate,
    onError: onTenureError,
  });

  const [category, setCategory] = useState<MemberCategory | "">(memberData.category ?? "");
  const [status, setStatus] = useState<MemberStatus | "">(memberData.status ?? "");
  const [isBaby, setIsBaby] = useState(memberData.is_baby ?? false);
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoSaved, setInfoSaved] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  const handleInfoSave = async () => {
    setInfoSaving(true);
    setInfoSaved(false);
    setInfoError(null);
    try {
      await updateMember.mutateAsync({
        id: memberId,
        category: category || null,
        status: status || null,
        is_baby: isBaby,
      });
      setInfoSaved(true);
      setTimeout(() => setInfoSaved(false), 2000);
    } catch (err) {
      setInfoError(extractMessage(err));
    } finally {
      setInfoSaving(false);
    }
  };

  const handleShowOnWebsiteToggle = async (checked: boolean) => {
    await updateMember.mutateAsync({ id: memberId, show_on_website: checked });
  };

  const officerPositions = MEMBER_POSITIONS.filter((p) => p !== MemberPosition.Member);

  return (
    <CardContent className="space-y-8">
      {/* Member Info */}
      <section className="space-y-4 max-w-sm">
        <h3 className="font-semibold">Member info</h3>
        <div className="space-y-1.5">
          <Label>Category</Label>
          <Select
            value={category || "none"}
            onValueChange={(v) => setCategory(v === "none" ? "" : (v as MemberCategory))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {MEMBER_CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {MEMBER_CATEGORY_LABELS[c]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select
            value={status || "none"}
            onValueChange={(v) => setStatus(v === "none" ? "" : (v as MemberStatus))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {MEMBER_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {MEMBER_STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is-baby"
            checked={isBaby}
            onChange={(e) => setIsBaby(e.target.checked)}
            className="rounded"
          />
          <Label htmlFor="is-baby">Baby member</Label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="show-on-website"
            checked={memberData.show_on_website ?? false}
            onChange={(e) => handleShowOnWebsiteToggle(e.target.checked)}
            disabled={updateMember.isPending}
            className="rounded"
          />
          <Label htmlFor="show-on-website">Show on website</Label>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleInfoSave} disabled={infoSaving}>
            {infoSaving ? "Saving…" : "Save"}
          </Button>
          {infoSaved && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
          {infoError && <span className="text-sm text-destructive">{infoError}</span>}
        </div>
      </section>

      <div className="border-t border-border pt-6 space-y-8">
        <h3 className="font-semibold text-muted-foreground uppercase tracking-wide text-xs">
          Tenure history
        </h3>

        {tenureError && (
          <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
            {tenureError}
            <button
              type="button"
              className="ml-2 underline text-xs"
              onClick={() => setTenureError(null)}
            >
              Dismiss
            </button>
          </p>
        )}

        {/* Membership Tenures */}
        <section className="space-y-3">
          <h3 className="font-semibold">Membership tenures</h3>
          <MembershipTenureTable
            tenures={membershipTenures}
            onCreate={(from, to) => createMembership.mutate({ member_id: memberId, from, to })}
            onUpdate={(id, from, to) => updateMembership.mutate({ id, from, to })}
            onDelete={(id) => deleteMembership.mutate({ id })}
          />
        </section>

        {/* Officer Tenures */}
        <section className="space-y-3">
          <h3 className="font-semibold">Officer tenures</h3>
          <OfficerTenureTable
            tenures={officerTenures}
            positions={officerPositions}
            onCreate={(position, from, to) =>
              createOfficer.mutate({
                member_id: memberId,
                position: position as MemberPosition,
                from,
                to,
              })
            }
            onUpdate={(id, position, from, to) =>
              updateOfficer.mutate({
                id,
                position: position as MemberPosition | undefined,
                from,
                to,
              })
            }
            onDelete={(id) => deleteOfficer.mutate({ id })}
          />
        </section>

        {/* LOA Tenures */}
        <section className="space-y-3">
          <h3 className="font-semibold">Leave of absence (LOA)</h3>
          <LoaTenureTable
            tenures={loaTenures}
            onCreate={(from, to, extended) =>
              createLoa.mutate({ member_id: memberId, from, to, extended })
            }
            onUpdate={(id, from, to, extended) => updateLoa.mutate({ id, from, to, extended })}
            onDelete={(id) => deleteLoa.mutate({ id })}
          />
        </section>
      </div>
    </CardContent>
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-1">
      <DatePicker value={value} onChange={onChange} className="w-36 text-sm" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 px-2 text-xs text-muted-foreground"
        onClick={() => onChange(today())}
        title="Set to today"
      >
        Today
      </Button>
    </div>
  );
}

function MembershipTenureTable({
  tenures,
  onCreate,
  onUpdate,
  onDelete,
}: {
  tenures: MembershipTenure[];
  onCreate: (from: string, to: string | null) => void;
  onUpdate: (id: string, from: string | undefined, to: string | null | undefined) => void;
  onDelete: (id: string) => void;
}) {
  const [newFrom, setNewFrom] = useState("");
  const [newTo, setNewTo] = useState("");

  return (
    <div className="space-y-2">
      {tenures.length === 0 && (
        <p className="text-sm text-amber-600 dark:text-amber-400 rounded-md border border-amber-300/50 bg-amber-50/50 dark:bg-amber-900/10 px-3 py-2">
          No membership tenure recorded. Add one below so &quot;Member since&quot; displays
          correctly.
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="max-sm:py-2.5">From</TableHead>
            <TableHead className="max-sm:py-2.5">To</TableHead>
            <TableHead className="w-8 max-sm:py-2.5" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenures.map((t) => (
            <MembershipTenureRow key={t.id} tenure={t} onUpdate={onUpdate} onDelete={onDelete} />
          ))}
          <TableRow>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <DateInput value={newFrom} onChange={setNewFrom} />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <DateInput value={newTo} onChange={setNewTo} />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <Button
                size="sm"
                variant="outline"
                disabled={!newFrom}
                onClick={() => {
                  onCreate(newFrom, newTo || null);
                  setNewFrom("");
                  setNewTo("");
                }}
              >
                Add
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function MembershipTenureRow({
  tenure,
  onUpdate,
  onDelete,
}: {
  tenure: MembershipTenure;
  onUpdate: (id: string, from: string | undefined, to: string | null | undefined) => void;
  onDelete: (id: string) => void;
}) {
  const [from, setFrom] = useState(tenure.from.slice(0, 10));
  const [to, setTo] = useState(tenure.to?.slice(0, 10) ?? "");

  const isDirty = from !== tenure.from.slice(0, 10) || to !== (tenure.to?.slice(0, 10) ?? "");

  return (
    <TableRow>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <DateInput value={from} onChange={setFrom} />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <DateInput value={to} onChange={setTo} />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <div className="flex items-center gap-1">
          {isDirty && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdate(tenure.id, from, to || null)}
            >
              Save
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(tenure.id)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function OfficerTenureTable({
  tenures,
  positions,
  onCreate,
  onUpdate,
  onDelete,
}: {
  tenures: OfficerTenure[];
  positions: string[];
  onCreate: (position: string, from: string, to: string | null) => void;
  onUpdate: (
    id: string,
    position: string | undefined,
    from: string | undefined,
    to: string | null | undefined,
  ) => void;
  onDelete: (id: string) => void;
}) {
  const [newPos, setNewPos] = useState("");
  const [newFrom, setNewFrom] = useState("");
  const [newTo, setNewTo] = useState("");

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="max-sm:py-2.5">Position</TableHead>
            <TableHead className="max-sm:py-2.5">From</TableHead>
            <TableHead className="max-sm:py-2.5">To</TableHead>
            <TableHead className="w-8 max-sm:py-2.5" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenures.map((t) => (
            <OfficerTenureRow
              key={t.id}
              tenure={t}
              positions={positions}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
          <TableRow>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <Select
                value={newPos || "none"}
                onValueChange={(v) => setNewPos(v === "none" ? "" : v)}
              >
                <SelectTrigger className="w-44 text-sm">
                  <SelectValue placeholder="Position" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Position</SelectItem>
                  {positions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <DateInput value={newFrom} onChange={setNewFrom} />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <DateInput value={newTo} onChange={setNewTo} />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <Button
                size="sm"
                variant="outline"
                disabled={!newPos || !newFrom}
                onClick={() => {
                  onCreate(newPos, newFrom, newTo || null);
                  setNewPos("");
                  setNewFrom("");
                  setNewTo("");
                }}
              >
                Add
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function OfficerTenureRow({
  tenure,
  positions,
  onUpdate,
  onDelete,
}: {
  tenure: OfficerTenure;
  positions: string[];
  onUpdate: (
    id: string,
    position: string | undefined,
    from: string | undefined,
    to: string | null | undefined,
  ) => void;
  onDelete: (id: string) => void;
}) {
  const [pos, setPos] = useState(tenure.position as string);
  const [from, setFrom] = useState(tenure.from.slice(0, 10));
  const [to, setTo] = useState(tenure.to?.slice(0, 10) ?? "");

  const isDirty =
    pos !== tenure.position ||
    from !== tenure.from.slice(0, 10) ||
    to !== (tenure.to?.slice(0, 10) ?? "");

  return (
    <TableRow>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <Select value={pos} onValueChange={setPos}>
          <SelectTrigger className="w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {positions.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <DateInput value={from} onChange={setFrom} />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <DateInput value={to} onChange={setTo} />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <div className="flex items-center gap-1">
          {isDirty && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdate(tenure.id, pos, from, to || null)}
            >
              Save
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(tenure.id)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function LoaTenureTable({
  tenures,
  onCreate,
  onUpdate,
  onDelete,
}: {
  tenures: LoaTenure[];
  onCreate: (from: string, to: string | null, extended: boolean) => void;
  onUpdate: (
    id: string,
    from: string | undefined,
    to: string | null | undefined,
    extended: boolean | undefined,
  ) => void;
  onDelete: (id: string) => void;
}) {
  const [newFrom, setNewFrom] = useState("");
  const [newTo, setNewTo] = useState("");
  const [newExtended, setNewExtended] = useState(false);

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="max-sm:py-2.5">From</TableHead>
            <TableHead className="max-sm:py-2.5">To</TableHead>
            <TableHead className="max-sm:py-2.5">Extended</TableHead>
            <TableHead className="w-8 max-sm:py-2.5" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tenures.map((t) => (
            <LoaTenureRow key={t.id} tenure={t} onUpdate={onUpdate} onDelete={onDelete} />
          ))}
          <TableRow>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <DateInput value={newFrom} onChange={setNewFrom} />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <DateInput value={newTo} onChange={setNewTo} />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <input
                type="checkbox"
                checked={newExtended}
                onChange={(e) => setNewExtended(e.target.checked)}
                className="rounded"
              />
            </TableCell>
            <TableCell className="px-2 py-1 max-sm:py-2.5">
              <Button
                size="sm"
                variant="outline"
                disabled={!newFrom}
                onClick={() => {
                  onCreate(newFrom, newTo || null, newExtended);
                  setNewFrom("");
                  setNewTo("");
                  setNewExtended(false);
                }}
              >
                Add
              </Button>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function LoaTenureRow({
  tenure,
  onUpdate,
  onDelete,
}: {
  tenure: LoaTenure;
  onUpdate: (
    id: string,
    from: string | undefined,
    to: string | null | undefined,
    extended: boolean | undefined,
  ) => void;
  onDelete: (id: string) => void;
}) {
  const [from, setFrom] = useState(tenure.from.slice(0, 10));
  const [to, setTo] = useState(tenure.to?.slice(0, 10) ?? "");
  const [extended, setExtended] = useState(tenure.extended);

  const isDirty =
    from !== tenure.from.slice(0, 10) ||
    to !== (tenure.to?.slice(0, 10) ?? "") ||
    extended !== tenure.extended;

  return (
    <TableRow>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <DateInput value={from} onChange={setFrom} />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <DateInput value={to} onChange={setTo} />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <input
          type="checkbox"
          checked={extended}
          onChange={(e) => setExtended(e.target.checked)}
          className="rounded"
        />
      </TableCell>
      <TableCell className="px-2 py-1 max-sm:py-2.5">
        <div className="flex items-center gap-1">
          {isDirty && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onUpdate(tenure.id, from, to || null, extended)}
            >
              Save
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(tenure.id)}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
